<template>
  <div>
    <FilterOverview title="Yield Controlling" :showFilterInitially="false" @reload="onReload">
      <template #toolbar-left>
        <div
          class="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap"
          style="gap: 12px"
        >
          <RegionFilterButtonGroup
            :regions="['Amerika', 'Afrika', 'Asien', 'Orient', 'Ozeanien', 'Nordeuropa', 'Südeuropa']"
            :disabled="regionFilterDisabled"
            v-model="selectedRegions"
            :width="520"
          ></RegionFilterButtonGroup>
          <b-form-checkbox v-model="vergangeneTermineAusblenden" @change="gridApi.onFilterChanged()" switch>
            Vergangene Termine ausblenden
          </b-form-checkbox>

          <!-- Filter Reset Button -->
          <div
            v-if="isFilterSupplied"
            class="icon-button d-flex justify-content-center text-danger mr-4 ml-2"
            @click="resetAllFilters"
            v-b-tooltip.hover.bottom
            title="Filter zurücksetzen"
          >
            <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
          </div>
        </div>
      </template>
      <template #table="{ tableHeight }">
        <AgGridVue
          :style="{ height: tableHeight + 87 + 'px' }"
          :rowModelType="'serverSide'"
          :getRowId="getRowId"
          :statusBar="statusBar"
          :columnDefs="columnDefs"
          :rowSelection="'multiple'"
          :rowMultiSelectWithClick="'true'"
          :tooltipShowDelay="0"
          :defaultColDef="defaultColDef"
          :enableCellTextSelection="true"
          @grid-ready="onGridReady"
          @rowSelected="onRowSelected"
          @rowDoubleClicked="onRowDoubleClicked"
          rowHeight="50px"
          class="ag-theme-alpine m-0 p-0"
          suppressContextMenu
          suppressCellFocus
        ></AgGridVue>
        <YieldControllingSidebar
          :reiseterminId="activeReisetermin?.id"
          @sidebarHide="onSidebarHide"
          @showModalAufgabeEdit="onShowModalAufgabeEdit"
          @rowSelectionChanged="changeRowSelection"
          @reload="onReload"
          @thWerteBearbeitet="onThWerteBearbeitet"
        ></YieldControllingSidebar>
      </template>
      <template #toolbar-right>
        <b-button
          size="sm"
          class="icon-button d-inline-flex align-items-center justify-content-center p-0"
          v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
          title="Excel-Export"
          @click="onButtonExport"
        >
          <Download :size="20" />
        </b-button>
        <div
          v-if="allSelectedRows.length > 0"
          class="icon-button d-inline-flex justify-content-center align-middle ml-2"
          @click="openAufgabenModalWithData"
          v-b-tooltip.hover.bottom
          :title="'Aufgabe erstellen'"
        >
          <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
        </div>
      </template>
    </FilterOverview>
    <AufgabeErstellenModal
      :prefixedVerlinkungen="prefixedVerlinkungen"
      :disableAttachments="false"
      :fromFVC="false"
      :enableManualTask="true"
    />
  </div>
</template>

<script>
import buildQuery from 'odata-query';

import FilterOverview from '@/components/common/filter-overview.vue';
import RegionFilterButtonGroup from '@/components/common/region-filter-button-group.vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import {
  formatDateWithoutHours,
  reiseterminStateFormatter,
  reiseterminStatePillHtml,
  reiseterminStateFormatterSmallHtml,
  translateStatusGruppeOptions,
  translateEinkaufsartOptions,
} from '@/core/common/helpers/utils.js';
import CustomInfoIconHeader from '@/components/common/custom-info-icon-header.vue';
import CustomIconExclamation from '@/components/common/custom-icon-exclamation.vue';
import CellRendererFlugverfuegbarkeiten from '@/components/produkte/reisetermine/cell-renderer-flugverfuegbarkeiten.vue';
import CellRendererZimmerkontingente from '@/components/common/zimmerkontingente/cell-renderer-zimmerkontingente.vue';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import { mapGetters, mapState } from 'vuex';
import {
  yieldControllingSelectedReiseterminVariables,
  yieldControllingExpandConfig,
  yieldControllingExportExpandConfig,
  yieldControllingHeaderRow,
  createExcelExport,
} from '@/core/common/helpers/exportHelper.js';
import * as odataService from '@/core/common/services/odata.service';
import { add, max } from 'date-fns';
import AgDateRangePicker from '@/components/flugverfuegbarkeit/ag-date-range-picker.vue';
import CellRendererPax from '@/components/produkte/reisetermine/cell-renderer-pax.vue';
import { vorzeitigGarantiertHtml, paxFilterFormatterSmallHtml } from '@/core/common/helpers/utils';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import FilterRendererInlandsflugEinkauf from '@/components/common/filter-renderer-inlandsflug-einkauf.vue';
import YieldControllingSidebar from './sidebar-yield-controlling.vue';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import { format, sub, parseISO, getISODay, differenceInDays } from 'date-fns';
import CellRendererStatus from '@/components/flugverfuegbarkeit/cell-renderer-status.vue';
import { GET_FILTER_OPTIONS } from '@/core/produkte/reisetermine/stores/reisetermine.module';
import { CircleCheck, Download, FunnelX, ListChecks, Plane, CircleX } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';

export default {
  components: {
    Download,
    ListChecks,
    FilterOverview,
    RegionFilterButtonGroup,
    AgGridVue,
    CustomInfoIconHeader,
    CustomIconExclamation,
    CellRendererFlugverfuegbarkeiten,
    CellRendererZimmerkontingente,
    StatusBarComponent,
    AgDateRangePicker,
    CellRendererPax,
    CustomTooltip,
    FilterRendererInlandsflugEinkauf,
    YieldControllingSidebar,
    AufgabeErstellenModal,
    CellRendererStatus,
    FunnelX,
  },

  data() {
    return {
      gridApi: null,
      columnDefs: null,
      gridColumnApi: null,
      vergangeneTermineAusblenden: true,
      regionFilterDisabled: true,
      selectedRegions: [],
      allSelectedRows: [],
      count: null,
      statusBar: null,
      isFilterSupplied: false,
      lastOdataFilter: null,
      selectedRowID: null,
      selectionToRestore: null,
      activeReisetermin: null,
      prefixedVerlinkungen: [],
      defaultColDef: {
        flex: 1,
        filter: true,
        sortable: false,
        resizable: false,
        floatingFilter: true,
        suppressMenu: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          defaultToNothingSelected: true,
        },
        tooltipComponent: 'CustomTooltip',
      },
      paxFilterFormatterSmallHtml,
    };
  },
  watch: {
    // schaut auf das array "selectedRegions" und dessen veränderung
    selectedRegions(newSelectedRegions) {
      // und geben das newSelectedRegions aus dem emit in die methode
      this.changeRegionFilterInstance(newSelectedRegions);
      this.checkIfFilterSupplied();
    },
    vergangeneTermineAusblenden(newValue) {
      this.updateStatusFilter(newValue);
      this.checkIfFilterSupplied();
    },
  },
  created() {
    this.statusBar = {
      statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
    };
    this.columnDefs = [
      {
        field: 'istZusatztermin',
        headerName: '',
        filter: 'agSetColumnFilter',
        cellRenderer: params => {
          if (params.value) {
            return 'Z';
          }
          return '';
        },
        filterParams: {
          values: [true, false],
          valueFormatter: params =>
            params.value === true
              ? 'Zusatztermin'
              : params.value === false
              ? 'Kein Zusatztermin'
              : params.value,
        },
        width: 65,
        minWidth: 65,
        pinned: 'left',
      },
      {
        checkboxSelection: true,
        headerCheckboxSelection: true,
        field: 'reiseterminkuerzel',
        headerName: 'Reise',
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['apply'],
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['reiseterminkuerzel'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.reiseterminkuerzel.substring(0, 6));
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        valueFormatter: params => params.value.substring(0, 6),
        sortable: true,
        minWidth: 150,
        width: 150,
        pinned: 'left',
      },
      {
        field: 'startdatum',
        headerName: 'Startdatum',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 170,
        width: 170,
        pinned: 'left',
      },
      {
        field: 'state',
        headerName: 'Status',
        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          valueFormatter: params => reiseterminStateFormatter[params.value],
          cellRenderer: params => reiseterminStateFormatterSmallHtml[params.value] ?? params.value,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['state'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.state);
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        valueFormatter: params => reiseterminStateFormatter[params.value],
        cellRenderer: params => (params.value ? reiseterminStatePillHtml(params.value) : '-'),
        minWidth: 150,
        width: 150,
      },
      {
        field: 'abreisedatum',
        headerName: 'Abreise',
        sortable: true,
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 170,
        width: 170,
      },
      {
        field: 'verkehrstagHin',
        headerName: 'VT Hinflug',
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          refreshValuesOnOpen: true,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['id', 'verkehrstagHin'],
            });
            const query = { odataFilter: lastOdataFilterBuilt };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(vt => vt.verkehrstagHin);
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        width: 90,
        minWidth: 90,
      },
      {
        field: 'ziellandZielflughaefen',
        headerName: 'ZL Zielflughäfen',
        cellRenderer: params => {
          const { ziellandZielflughaefen } = params.data.metadata;
          return ziellandZielflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? '';
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              expand: {
                metadata: {
                  select: ['id'],
                  expand: {
                    ziellandZielflughaefen: {
                      select: ['flughafenIataCode'],
                    },
                  },
                },
              },
              select: ['id'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result
              .map(item => item.metadata.ziellandZielflughaefen.map(item => item.flughafenIataCode))
              .flat();
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        minWidth: 135,
        width: 135,
      },
      {
        field: 'hotelendUndRueckflugdatum',
        headerName: 'Rückflugdatum',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 170,
        width: 170,
      },
      {
        field: 'verkehrstagRueck',
        headerName: 'VT Rückflug',
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          refreshValuesOnOpen: true,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['id', 'verkehrstagRueck'],
            });
            const query = { odataFilter: lastOdataFilterBuilt };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(vt => vt.verkehrstagRueck);
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        width: 100,
        minWidth: 100,
      },
      {
        field: 'ziellandAbflughaefen',
        headerName: 'ZL Abflughäfen',
        cellRenderer: params => {
          const { ziellandAbflughaefen } = params.data.metadata;
          return ziellandAbflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? '';
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          refreshValuesOnOpen: true,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              expand: {
                metadata: {
                  select: ['id'],
                  expand: {
                    ziellandAbflughaefen: {
                      select: ['flughafenIataCode'],
                    },
                  },
                },
              },
              select: ['id'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result
              .map(item => item.metadata.ziellandAbflughaefen.map(item => item.flughafenIataCode))
              .flat();
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
        },
        minWidth: 135,
        width: 135,
      },
      {
        field: 'metadata.inlandsfluegeEinkauf',
        headerName: 'INFL',
        minWidth: 70,
        width: 70,
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              expand: {
                metadata: {
                  select: ['inlandsfluegeEinkauf'],
                },
              },
              select: ['id'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.metadata?.inlandsfluegeEinkauf);
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
          cellRenderer: 'FilterRendererInlandsflugEinkauf',
          comparator: (a, b) => {
            const ranking = {
              KeinInlandsflug: 0,
              NurSkr: 1,
              NurDmc: 2,
              Gemischt: 3,
            };

            if (a === 'null') return 1;
            else if (ranking[a] === ranking[b]) return 0;
            return ranking[a] > ranking[b] ? 1 : -1;
          },
        },
        cellRenderer: params => {
          const { inlandsfluege } = params.data.metadata;
          const nurSkrInlandsfluege = inlandsfluege.every(inlandsflug => inlandsflug.einkauf === 'Skr');
          const nurDmcInlandsfluege = inlandsfluege.every(inlandsflug => inlandsflug.einkauf === 'Dmc');
          const skrOderDmcInlandsfluege = inlandsfluege.every(
            inlandsflug => inlandsflug.einkauf === 'Dmc' || inlandsflug.einkauf === 'Skr'
          );
          if (inlandsfluege.length === 0) return '';
          else if (nurSkrInlandsfluege) return lucideHtml(Plane, { size: 20, class: 'text-danger' });
          else if (nurDmcInlandsfluege) return lucideHtml(Plane, { size: 20, style: 'color: #000' });
          else if (skrOderDmcInlandsfluege) return lucideHtml(Plane, { size: 20, style: 'color: #A6A6A8' });
        },
        floatingFilter: true,
        resizable: false,
      },
      {
        field: 'paxWidget',
        headerName: 'PAX',
        cellRenderer: 'CellRendererPax',
        filter: 'agSetColumnFilter',
        minWidth: 160,
        maxWidth: 180,
        suppressRowTransform: true,
        filterParams: {
          comparator: (a, b) => {
            const ranking = {
              'Ist-Pax = 0': 0,
              'Ist-Pax > 0': 1,
              'Ist-Pax = 1': 2,
              'Ist-Pax = Max-Pax': 3,
              'Ist-Pax < MTN-Pax': 4,
              'Ist-Pax ≥ MTN-Pax': 5,
              'Ist-Pax = MTN-1': 6,
              'Ist-Pax = MTN-2': 7,
              'Ist-Pax = Kalk-Pax': 8,
            };

            if (a === 'null') return 1;
            else if (ranking[a] === ranking[b]) return 0;
            return ranking[a] > ranking[b] ? 1 : -1;
          },
          values: [
            'Ist-Pax = 0',
            'Ist-Pax > 0',
            'Ist-Pax = 1',
            'Ist-Pax = Max-Pax',
            'Ist-Pax < MTN-Pax',
            'Ist-Pax ≥ MTN-Pax',
            'Ist-Pax = MTN-1',
            'Ist-Pax = MTN-2',
            'Ist-Pax = Kalk-Pax',
          ],
          debounceMs: 1000,
          valueFormatter: params => (params.value === 'InArbeit' ? 'In Arbeit' : params.value),
          cellRenderer: params => this.paxFilterFormatterSmallHtml[params.value] ?? '(Select all)',
        },
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'justify-items': 'center',
          overflow: 'visible',
          'align-items': 'center',
        },
        cellRendererParams: params => ({
          paxObject: params.data.pax,
          minPax: params.data.metadata.minPax,
          maxPax: params.data.metadata.maxPax,
          kalkPax: params.data.metadata.kalkPax,
          forecastPax: params.data.metadata.forecastPax,
          durchfuehrbareMinPax: params.data.metadata.durchfuehrbareMinPax,
          id: params.data.id,
        }),
      },
      {
        field: 'metadata.planPax',
        headerName: 'Plan Pax',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        minWidth: 100,
        maxWidth: 100,
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        cellRenderer: params => (params.value != null ? params.value : '-'),
      },
      {
        field: 'forecastPaxPotenzial',
        headerName: 'FC Pax Potenzial',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        minWidth: 160,
        maxWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        cellRenderer: params => (params.value != null ? params.value : '-'),
      },
      {
        field: 'stopSale',
        headerName: 'Stop Sale',
        minWidth: 100,
        width: 100,
        cellRenderer: 'CustomIconExclamation',
        cellStyle: { alignContent: 'center' },
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['stopSale'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.stopSale);
            const uniqueOptions = [...new Set(options)].filter(val => val !== null && val !== undefined);

            params.success(uniqueOptions);
          },
          cellRenderer: 'CustomIconExclamation',
          cellRendererParams: { compact: true },
        },
      },
      {
        field: 'vorzeitigGarantiert',
        headerName: 'Vorz. gar.',
        minWidth: 85,
        maxWidth: 85,
        cellRenderer: params => {
          return this.vorzeitigGarantiertHtml(params.value);
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['vorzeitigGarantiert'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.vorzeitigGarantiert);
            const uniqueOptions = [...new Set(options)];

            params.success(uniqueOptions);
          },
          valueFormatter: params =>
            params.value === 'VorzeitigGarantiert'
              ? 'Vorzeitig garantiert'
              : params.value === 'WarVorzeitigGarantiert'
              ? 'War vorzeitig garantiert'
              : params.value === 'NichtVorzeitigGarantiert'
              ? 'Nicht vorzeitig garantiert'
              : params.value,
        },
      },
      {
        field: 'garantiert',
        headerName: 'Gar.',
        minWidth: 50,
        width: 50,
        cellRenderer: params => {
          return params.value
            ? lucideHtml(CircleCheck, { size: 20, class: 'text-success' })
            : lucideHtml(CircleX, { size: 20, class: 'text-danger' });
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['garantiert'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.garantiert);
            const uniqueOptions = [...new Set(options)].filter(val => val !== null && val !== undefined);

            params.success(uniqueOptions);
          },
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value === true
              ? lucideHtml(CircleCheck, { class: 'text-success' })
              : lucideHtml(CircleX, { class: 'text-danger' }),
        },
      },
      {
        field: 'durchfuehrungUnterMtzDurchSkr',
        headerName: 'Durchführung unter MTZ (SKR)',
        minWidth: 140,
        width: 140,
        cellRenderer: params => {
          return params.value
            ? lucideHtml(CircleCheck, { size: 20, class: 'text-success' })
            : lucideHtml(CircleX, { size: 20, class: 'text-danger' });
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              select: ['durchfuehrungUnterMtzDurchSkr'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.map(item => item.durchfuehrungUnterMtzDurchSkr);
            const uniqueOptions = [...new Set(options)].filter(val => val !== null && val !== undefined);

            params.success(uniqueOptions);
          },
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value === true
              ? lucideHtml(CircleCheck, { class: 'text-success' })
              : lucideHtml(CircleX, { class: 'text-danger' }),
        },
      },
      {
        field: 'releasedatum',
        headerName: 'Release (TbR)',
        filter: 'AgDateRangePicker',
        // valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 170,
        maxWidth: 170,
        sortable: true,
        floatingFilter: true,
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        cellRenderer: params => {
          const daysUntilRelease = differenceInDays(parseISO(params.value), new Date());
          return formatDateWithoutHours(params.value) + ' (' + daysUntilRelease + ')';
        },
      },
      {
        field: 'basisPreis',
        headerName: 'DZ p.P.',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        minWidth: 110,
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        cellRenderer: params => (params.value ? this.$options.filters.currency(params.value) : '-'),
      },
      {
        field: 'thLangstrecke',
        headerName: 'TH Langstrecke',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        cellRenderer: params => (params.value ? params.value + '€' : '-'),
        width: 145,
        minWidth: 145,
      },
      {
        field: 'gueltigerSaisonpreis.thMaxLangstrecke',
        headerName: 'TH Max Langstrecke',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        cellRenderer: params => (params.value ? params.value + '€' : '-'),
        cellStyle: params => {
          if (
            params.data?.gueltigerSaisonpreis?.thMaxLangstrecke !== null &&
            params.data?.thLangstrecke !== null &&
            params.data.gueltigerSaisonpreis?.thMaxLangstrecke > params.data.thLangstrecke
          ) {
            return { color: 'green' };
          }
        },
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        width: 175,
        minWidth: 175,
      },
      {
        field: 'initialThLangstrecke',
        headerName: 'Initial TH Langstrecke',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        cellRenderer: params => (params.value ? params.value + '€' : '-'),
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        width: 215,
        minWidth: 215,
      },
      {
        field: 'flugverfuegbarkeitFRA',
        headerName: 'Flugverfügbarkeit FRA',
        sortable: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['NichtVerfuegbar', 'Verfuegbar<200', 'AufAnfrage', 'Verfuegbar>200'],
          debounceMs: 1000,
          cellRenderer: 'CellRendererStatus',
          cellRendererParams: {
            isFilterRenderer: true,
          },
        },
        cellRenderer: 'CellRendererFlugverfuegbarkeiten',
        valueGetter: params => {
          const fraVerfuegbarkeit = params.data?.flugverfuegbarkeiten?.find(
            flugverfuegbarkeit => flugverfuegbarkeit.isNeusteFraVerfuegbarkeit === true
          );
          return fraVerfuegbarkeit ? [fraVerfuegbarkeit] : [];
        },
        width: 150,
        minWidth: 150,
      },
      {
        field: 'flugverfuegbarkeiten',
        headerName: 'Flugverfügbarkeit',
        cellRenderer: 'CellRendererFlugverfuegbarkeiten',
        cellRendererParams: {
          excludeAirports: ['FRA', 'GVA'],
        },
        width: 450,
        minWidth: 450,
        flex: 1,
        filter: false,
      },
      {
        headerName: 'Zimmerkontingente',
        field: 'zimmerkontingente',
        cellRenderer: 'CellRendererZimmerkontingente',
        width: 200,
        minWidth: 200,
        cellRendererParams: { isZvc: true },
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'align-items': 'center',
          'justify-items': 'center',
          overflow: 'visible',
        },
        filter: false,
      },
      {
        field: 'countryManagers',
        headerName: 'CM',
        cellRenderer: params =>
          params.value?.length ? params.value.map(cm => cm.countryManagerKuerzel).join(', ') : '-',
        tooltipValueGetter: params =>
          params.value && params.value.length > 1
            ? params.value.map(cm => cm.countryManagerKuerzel).join(', ')
            : null,
        minWidth: 100,
        maxWidth: 100,
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          debounceMs: 1000,
          values: async params => {
            const lastOdataFilterBuilt = buildQuery({
              filter: this.lastOdataFilter,
              expand: {
                countryManagers: { select: ['countryManagerKuerzel'] },
              },
              select: ['id'],
            });
            const query = {
              odataFilter: lastOdataFilterBuilt,
            };
            const result = await this.$store.dispatch('overview/' + GET_FILTER_OPTIONS, query);
            const options = result.flatMap(item =>
              (item.countryManagers ?? []).map(cm => cm.countryManagerKuerzel)
            );
            const uniqueOptions = [...new Set(options)].filter(Boolean).sort();

            params.success(uniqueOptions);
          },
        },
      },
      {
        field: 'reise.region',
        headerName: 'Region',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Amerika', 'Afrika', 'Asien', 'Orient', 'Ozeanien', 'Nordeuropa', 'Südeuropa'],
          debounceMs: 1000,
        },
        hide: true,
      },
    ];
  },
  computed: {
    ...mapGetters(['getReisekuerzelOptions']),
    ...mapState({
      alleFlughaefen: state => state.flughaefen.flughaefen,
    }),
    reisenOptions() {
      return this.getReisekuerzelOptions.map(r => r.reisekuerzel);
    },
    alleFlughaefenIataCode() {
      return this.alleFlughaefen?.map(flughafen => flughafen.iataCode);
    },
  },
  methods: {
    vorzeitigGarantiertHtml,
    getRowId(params) {
      return String(params.data.id);
    },
    onReload() {
      // Die Auswahl wird gemerkt
      this.selectionToRestore = this.gridApi?.getSelectedNodes().map(node => node.id);
      this.gridApi?.refreshServerSide({ purge: true });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      this.gridApi?.onFilterChanged();

      const updateData = () => {
        const server = this.server();
        const datasource = this.createDatasource(server);
        params.api.setServerSideDatasource(datasource);
      };

      updateData();
    },
    createDatasource(server) {
      return {
        getRows: async params => {
          console.log('[Datasource] - rows requested by grid: ', params.request);
          const response = await server.getData(params.request);
          if (response.success) {
            params.success({
              rowData: response.rows,
            });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
            // Nach einem Reload wird die vorherige Auswahl wiederhergestellt
            if (this.selectionToRestore) {
              this.selectionToRestore.forEach(id => this.gridApi.getRowNode(id)?.setSelected(true));
              this.selectionToRestore = null;
            }
          } else {
            params.fail();
          }
        },
      };
    },
    server() {
      return {
        getData: async request => {
          this.checkIfFilterSupplied();
          const odataFilter = this.getOdataFilterFromAgGridRequest(request);
          this.lastOdataFilter = odataFilter;
          const selectedReiseterminVariables = [...yieldControllingSelectedReiseterminVariables, 'id'];
          const expand = {
            ...yieldControllingExpandConfig,
          };

          const response = await odataService.getReisetermin({
            filter: odataFilter,
            top: request.endRow - request.startRow,
            select: selectedReiseterminVariables,
            skip: request.startRow,
            count: true,
            expand,
            ...(request.sortModel.length > 0
              ? {
                  orderBy: request.sortModel.map(s => s.colId.replaceAll('.', '/') + ' ' + s.sort),
                }
              : { orderBy: 'reise/prioritaet asc, reise/reisekuerzel asc, abreisedatum asc' }),
          });

          this.count = response.count;
          this.regionFilterDisabled = false;

          const filterInstance = this.gridApi.getFilterInstance('reiseterminkuerzel');
          if (filterInstance) {
            filterInstance.refreshFilterValues();
          }

          return {
            success: true,
            rows: response.data,
          };
        },
      };
    },
    getOdataFilterFromAgGridRequest(request) {
      const filterKeys = Object.keys(request.filterModel);
      let filters = [];
      const allFilterKeys = filterKeys.map(key => {
        const formattedKey = key.replaceAll('.', '/');
        if (request.filterModel[key].filterType === 'text') {
          if (
            [
              'thLangstrecke',
              'gueltigerSaisonpreis/thMaxLangstrecke',
              'initialThLangstrecke',
              'basisPreis',
              'metadata/planPax',
              'forecastPaxPotenzial',
            ].includes(formattedKey)
          ) {
            if (request.filterModel[key].filter === '-') {
              return { [formattedKey]: null };
            } else if (request.filterModel[key].filter.includes('-')) {
              const min = parseInt(request.filterModel[key].filter.split('-')[0]);
              const max = parseInt(request.filterModel[key].filter.split('-')[1]);
              return { [formattedKey]: { le: max, ge: min } };
            } else if (request.filterModel[key].filter[0] === '>') {
              const min = parseInt(request.filterModel[key].filter.split('>')[1]);
              return { [formattedKey]: { gt: min } };
            } else if (request.filterModel[key].filter[0] === '<') {
              const max = parseInt(request.filterModel[key].filter.split('<')[1]);
              return { [formattedKey]: { lt: max } };
            } else return { [formattedKey]: parseInt(request.filterModel[key].filter) };
          }
        } else if (request.filterModel[key].filterType === 'set') {
          if (key === 'reiseterminkuerzel') {
            return { 'reise/reisekuerzel': { in: request.filterModel[key].values } };
          } else if (key === 'inlandsflugEinkauf') {
            const odataFlugeinkaufFormatter = {
              Gemischt:
                "metadata/inlandsfluege/any(inlandsfluege:inlandsfluege/einkauf eq 'Dmc') and metadata/inlandsfluege/any(inlandsfluege:inlandsfluege/einkauf eq 'Skr') and metadata/inlandsfluege/any()",
              NurSkr:
                "metadata/inlandsfluege/all(inlandsfluege:inlandsfluege/einkauf eq 'Skr') and metadata/inlandsfluege/any()",
              NurDmc:
                "metadata/inlandsfluege/all(inlandsfluege:inlandsfluege/einkauf eq 'Dmc') and metadata/inlandsfluege/any()",
              KeinInlandsflug: 'not Metadata/inlandsfluege/any()',
            };
            return (
              '(' +
              request.filterModel[key].values.map(value => odataFlugeinkaufFormatter[value]).join(' or ') +
              ')'
            );
          } else if (formattedKey === 'paxWidget') {
            const { values } = request.filterModel[key];
            const allPaxOptions = [
              'Ist-Pax = 0',
              'Ist-Pax > 0',
              'Ist-Pax = 1',
              'Ist-Pax = Max-Pax',
              'Ist-Pax < MTN-Pax',
              'Ist-Pax ≥ MTN-Pax',
              'Ist-Pax = MTN-1',
              'Ist-Pax = MTN-2',
              'Ist-Pax = Kalk-Pax',
            ];

            // Wenn alle Optionen ausgewählt sind, keinen Filter setzen
            if (values.length === allPaxOptions.length || values.length === 0) {
              return null;
            }

            const paxFilterConverter = {
              'Ist-Pax = 0': { ['pax/ist']: { eq: 0 } },
              'Ist-Pax > 0': { ['pax/ist']: { gt: 0 } },
              'Ist-Pax = 1': { ['pax/ist']: { eq: 1 } },
              'Ist-Pax = Max-Pax': { ['pax/ist']: { eq: { type: 'guid', value: 'pax/max' } } },
              'Ist-Pax < MTN-Pax': {
                and: [
                  { ['pax/ist']: { lt: { type: 'guid', value: 'pax/min' } } },
                  { ['pax/ist']: { gt: 0 } },
                ],
              },
              'Ist-Pax ≥ MTN-Pax': {
                and: [{ ['pax/ist']: { ge: { type: 'guid', value: 'pax/min' } } }],
              },
              'Ist-Pax = MTN-1': {
                and: [
                  { ['pax/min']: { eq: { type: 'guid', value: 'pax/ist add 1' } } },
                  { ['pax/ist']: { gt: 0 } },
                ],
              },
              'Ist-Pax = MTN-2': {
                and: [
                  { ['pax/min']: { eq: { type: 'guid', value: 'pax/ist add 2' } } },
                  { ['pax/ist']: { gt: 0 } },
                ],
              },
              'Ist-Pax = Kalk-Pax': { ['pax/ist']: { eq: { type: 'guid', value: 'metadata/kalkPax' } } },
            };

            if (values.length === 1) {
              return paxFilterConverter[values[0]];
            }

            return { or: [...values.map(value => paxFilterConverter[value])] };
          } else if (key === 'ziellandZielflughaefen' || key === 'ziellandAbflughaefen') {
            const { values } = request.filterModel[key];
            const formattedValues = values.map(flughafen => `'${flughafen}'`).join(',');
            const filterString = `metadata/${key}/any(flughafen: flughafen/flughafenIataCode in (${formattedValues}))`;
            return filterString;
          } else if (key === 'countryManagers') {
            const { values } = request.filterModel[key];
            const formattedValues = values.map(kuerzel => `'${kuerzel}'`).join(',');
            return `countryManagers/any(cm: cm/countryManagerKuerzel in (${formattedValues}))`;
          } else if (key === 'flugverfuegbarkeitFRA') {
            const conditions = request.filterModel[key].values.map(item => {
              if (item.includes('Verfuegbar<200')) {
                return "flugverfuegbarkeiten/any(flugverfuegbarkeit: flugverfuegbarkeit/isNeusteFraVerfuegbarkeit eq true and flugverfuegbarkeit/status eq 'Verfuegbar' and flugverfuegbarkeit/aufpreis le 200)";
              }
              if (item.includes('Verfuegbar>200')) {
                return "flugverfuegbarkeiten/any(flugverfuegbarkeit: flugverfuegbarkeit/isNeusteFraVerfuegbarkeit eq true and flugverfuegbarkeit/status eq 'Verfuegbar' and flugverfuegbarkeit/aufpreis gt 200)";
              } else {
                return `flugverfuegbarkeiten/any(flugverfuegbarkeit: flugverfuegbarkeit/isNeusteFraVerfuegbarkeit eq true and flugverfuegbarkeit/status eq '${item}')`;
              }
            });
            return '(' + conditions.join(' or ') + ')';
          } else {
            return { [formattedKey]: { in: request.filterModel[key].values } };
          }
        } else if (request.filterModel[key].filterType === 'date') {
          const dateFrom = new Date(request.filterModel[key].dateFrom.substring(0, 10));
          const dateTo = add(new Date(request.filterModel[key].dateTo), { hours: 23, minutes: 59 });

          return {
            [formattedKey]: {
              ge: dateFrom,
              le: dateTo,
            },
          };
        }
      });

      filters = [
        ...filters,
        {
          isDeleted: { ne: true },
        },
        ...allFilterKeys,
      ];

      if (this.vergangeneTermineAusblenden) {
        filters = [...filters, { abreisedatum: { ge: new Date() } }];
      }

      return filters;
    },

    resetAllFilters() {
      this.gridApi.setFilterModel(null);
      let filterDate1 = this.gridApi.getFilterInstance('startdatum');
      let filterDate2 = this.gridApi.getFilterInstance('abreisedatum');
      let filterDate3 = this.gridApi.getFilterInstance('releasedatum');
      let filterDate4 = this.gridApi.getFilterInstance('hotelendUndRueckflugdatum');

      filterDate1.onDateRangeFilterChanged(null);
      filterDate2.onDateRangeFilterChanged(null);
      filterDate3.onDateRangeFilterChanged(null);
      filterDate4.onDateRangeFilterChanged(null);

      this.selectedRegions = [];
      this.vergangeneTermineAusblenden = true;
    },
    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    checkIfFilterSupplied() {
      this.isFilterSupplied =
        (this.gridApi && Object.keys(this.gridApi?.getFilterModel()).length > 0) ||
        this.selectedRegions.length > 0 ||
        !this.vergangeneTermineAusblenden;
    },
    changeRegionFilterInstance(selectedRegions) {
      if (!this.gridApi) return;
      const regionsFilter = this.gridApi.getFilterInstance('reise.region');

      if (!regionsFilter) return;

      if (selectedRegions.length > 0) {
        regionsFilter.setModel({ values: selectedRegions });
      } else {
        regionsFilter.setModel({});
      }
      this.gridApi.onFilterChanged();
    },
    onRowDoubleClicked(params) {
      this.gridApi.deselectAll();
      this.gridApi.forEachNode(rowNode => {
        if (rowNode.rowIndex === params.rowIndex) {
          rowNode.setSelected(true);
          this.selectedRowID = params.rowIndex;
        }
      });
      this.activeReisetermin = params.data;
      this.$root.$emit('bv::toggle::collapse', 'sidebar-yield-controlling');
    },
    changeRowSelection(direction) {
      const displayedRows = this.gridApi?.getDisplayedRowCount();
      if (direction === 'up') {
        if (this.selectedRowID !== 0) {
          const targetedRowID = this.selectedRowID - 1;
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.rowIndex === targetedRowID) {
              rowNode.setSelected(true, true);
              this.gridApi.ensureIndexVisible(rowNode.rowIndex);
              this.activeReisetermin = rowNode.data;
              this.selectedRowID = targetedRowID;
            }
          });
        }
      } else if (direction === 'down') {
        if (this.selectedRowID + 1 < displayedRows) {
          const targetedRowID = this.selectedRowID + 1;
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.rowIndex === targetedRowID) {
              rowNode.setSelected(true, true);
              this.gridApi.ensureIndexVisible(rowNode.rowIndex);
              this.activeReisetermin = rowNode.data;
              this.selectedRowID = targetedRowID;
            }
          });
        }
      }
    },
    onSidebarHide() {
      this.activeReisetermin = null;
      this.gridApi.deselectAll();
    },
    onShowModalAufgabeEdit(prefixedVerlinkungen) {
      this.prefixedVerlinkungen = prefixedVerlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },
    onThWerteBearbeitet(response) {
      const result = response.data.result;

      this.gridApi.forEachNode(rowNode => {
        const matchingReiseterminkuerzel = result.find(
          r => r.reiseterminkuerzel === rowNode.data.reiseterminkuerzel
        );
        if (matchingReiseterminkuerzel) {
          rowNode.setDataValue('thLangstrecke', matchingReiseterminkuerzel.thLangstrecke);
          rowNode.setDataValue(
            'gueltigerSaisonpreis.thMaxLangstrecke',
            matchingReiseterminkuerzel.gueltigerSaisonpreis?.thMaxLangstrecke
          );
          // Der erste TH-Wert wird vom Core als Initialwert festgeschrieben
          rowNode.setDataValue('initialThLangstrecke', matchingReiseterminkuerzel.initialThLangstrecke);
        }
      });
    },

    createRowData(rt, currencyFilter) {
      const exportData = [];

      const vorzeitigGarantiert = value => {
        return value === 'VorzeitigGarantiert'
          ? 'Vorzeitig garantiert'
          : value === 'WarVorzeitigGarantiert'
          ? 'War vorzeitig garantiert'
          : value === 'NichtVorzeitigGarantiert'
          ? 'Nicht vorzeitig garantiert'
          : value;
      };

      const tageBisRelease = differenceInDays(parseISO(rt.releasedatum), new Date());

      const ziellandZielflughaefen = rt.metadata?.ziellandZielflughaefen
        .map(item => item.flughafenIataCode)
        .join(', ');
      const ziellandAbflughaefen = rt.metadata?.ziellandAbflughaefen
        .map(item => item.flughafenIataCode)
        .join(', ');

      const flightArrival = add(parseISO(rt.abreisedatum), {
        days: rt.metadata?.overnightOutbound || 0,
      });
      const fruehesteAnkunft = rt.metadata?.fruehesteAnkunft
        ? format(new Date(flightArrival), 'dd.MM.yyyy') + ' ' + rt.metadata?.fruehesteAnkunft.substring(0, 5)
        : '';
      const spaetesteAnkunft = rt.metadata?.spaetesteAnkunft
        ? format(
            add(new Date(flightArrival), { days: rt.metadata?.restriktionenHinRollover ? 1 : 0 }),
            'dd.MM.yyyy'
          ) +
          ' ' +
          rt.metadata?.spaetesteAnkunft.substring(0, 5)
        : '';

      const fruehesterAbflug = rt.metadata?.fruehesterAbflug?.substring(0, 5)
        ? format(new Date(rt.hotelendUndRueckflugdatum), 'dd.MM.yyyy') +
          ' ' +
          rt.metadata?.fruehesterAbflug?.substring(0, 5)
        : '';
      const spaetesterAbflug = rt.metadata?.spaetesterAbflug?.substring(0, 5)
        ? format(
            add(new Date(rt.hotelendUndRueckflugdatum), {
              days: rt.metadata?.restriktionenRueckRollover ? 1 : 0,
            }),
            'dd.MM.yyyy'
          ) +
          ' ' +
          rt.metadata?.spaetesterAbflug?.substring(0, 5)
        : '';

      const statusgruppe = translateStatusGruppeOptions(rt.flugeinkauf?.statusgruppe);

      const einkaufsart = translateEinkaufsartOptions(rt.flugeinkauf?.einkaufsart);

      const grpFH =
        !rt.flugeinkauf?.gruppeneinkaeufe || rt.flugeinkauf?.gruppeneinkaeufe.length === 0
          ? '-'
          : rt.flugeinkauf?.gruppeneinkaeufe
              .map(grp => grp.flughafenIataCode)
              .filter(Boolean)
              .join(', ');

      const gesamtpreis =
        !rt.flugeinkauf?.gruppeneinkaeufe || rt.flugeinkauf?.gruppeneinkaeufe.length === 0
          ? '-'
          : rt.flugeinkauf?.gruppeneinkaeufe
              .map(grp =>
                grp.gesamtpreisGruppeneinkauf ? `${Math.round(grp.gesamtpreisGruppeneinkauf)}€` : '0€'
              )
              .filter(Boolean)
              .join(', ');

      const fraVerfuegbarkeit = rt.flugverfuegbarkeiten?.find(
        flugverfuegbarkeit => flugverfuegbarkeit.isNeusteFraVerfuegbarkeit === true
      );
      const fraVerfuegbarkeitString = fraVerfuegbarkeit
        ? fraVerfuegbarkeit.aufpreis !== null
          ? fraVerfuegbarkeit.aufpreis === 0
            ? 'J'
            : fraVerfuegbarkeit.aufpreis + '€'
          : fraVerfuegbarkeit.status === 'AufAnfrage'
          ? 'RQ'
          : 'N'
        : '-';

      exportData.push([
        rt.istZusatztermin === true ? 'Zusatztermin' : 'Kein Zusatztermin',
        rt.reise?.region,
        rt.reise?.reisekuerzel,
        rt.reiseterminkuerzel,
        reiseterminStateFormatter[rt.state],
        formatDateWithoutHours(rt.abreisedatum),
        formatDateWithoutHours(rt.hotelendUndRueckflugdatum),
        rt.metadata?.minPax,
        rt.metadata?.durchfuehrbareMinPax,
        rt.metadata?.kalkPax,
        rt.metadata?.forecastPax,
        rt.pax?.ist,
        rt.pax?.opt,
        rt.pax?.anf,
        rt.pax?.max,
        rt.metadata?.planPax,
        rt.stopSale === true ? 'Ja' : 'Nein',
        vorzeitigGarantiert(rt.vorzeitigGarantiert),
        rt.garantiert === true ? 'Ja' : 'Nein',
        rt.durchfuehrungUnterMtzDurchSkr === true ? 'Ja' : 'Nein',
        formatDateWithoutHours(rt.releasedatum),
        tageBisRelease,
        currencyFilter(rt.basisPreis) ?? '',
        rt.metadata?.hotelnaechte,
        rt.metadata?.transferszenarioHin ?? '',
        rt.metadata?.transferszenarioRueck ?? '',
        ziellandZielflughaefen,
        ziellandAbflughaefen,
        rt.metadata?.inlandsfluegeEinkauf,
        '', // Abflug INFL
        '', // Strecke INFL
        rt.verkehrstagHin ?? '',
        rt.metadata.ziellandZielflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? '',
        fruehesteAnkunft,
        spaetesteAnkunft,
        rt.verkehrstagRueck ?? '',
        rt.metadata.ziellandAbflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? '',
        fruehesterAbflug,
        spaetesterAbflug,
        rt.thLangstrecke ? `${rt.thLangstrecke}€` : '0€',
        rt.thInlandsflug ? `${rt.thInlandsflug}€` : '0€',
        rt.gueltigerSaisonpreis?.thMaxLangstrecke ? `${rt.gueltigerSaisonpreis?.thMaxLangstrecke}€` : '0€',
        rt.gueltigerSaisonpreis?.thMaxInlandsflug ? `${rt.gueltigerSaisonpreis?.thMaxInlandsflug}€` : '0€',
        fraVerfuegbarkeitString,
        statusgruppe,
        einkaufsart,
        grpFH,
        gesamtpreis,
        rt.forecastPaxPotenzial ?? '-',
        rt.countryManagers?.length ? rt.countryManagers.map(cm => cm.countryManagerKuerzel).join(', ') : '-',
        rt.initialThLangstrecke ? `${rt.initialThLangstrecke}€` : '',
      ]);
      rt.metadata?.inlandsfluege
        ?.sort((a, b) => a.tag - b.tag)
        .forEach((inlf, i) => {
          const dateOfInlandsflug = add(new Date(rt.abreisedatum), { days: +inlf.tag - 1 });
          const verkehrstagOfInlandsflug = getISODay(dateOfInlandsflug);
          exportData.push([
            '',
            '',
            '',
            '',
            `${rt.reiseterminkuerzel}_${i + 1}`,
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            formatDateWithoutHours(dateOfInlandsflug),
            inlf.abflughafenIataCode + ' - ' + inlf.zielflughafenIataCode,
            verkehrstagOfInlandsflug, // VT Hinflug Inlf <- berechnung wie in der Flugplanung
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            inlf.einkauf ?? '-',
            '',
            '',
            '',
            '',
            '',
          ]);
        });

      return exportData;
    },

    async onButtonExport() {
      await createExcelExport({
        headerRow: yieldControllingHeaderRow,
        fetchData: async () =>
          await odataService.getReisetermin({
            filter: this.lastOdataFilter,
            select: yieldControllingSelectedReiseterminVariables,
            count: true,
            expand: yieldControllingExportExpandConfig,
            orderBy: 'startdatum asc',
          }),
        createRowData: rt => this.createRowData(rt, this.$options.filters.currency),
        sheetName: 'Yield-Controlling',
        fileNamePrefix: 'Yield-Controlling_Export',
      });
    },
    openAufgabenModalWithData() {
      this.prefixedVerlinkungen = this.allSelectedRows.map((item, i) => ({
        id: i,
        typ: 'Reisetermin',
        artifactId: item.data.id,
        artifactText: item.data.reiseterminkuerzel,
      }));

      this.$bvModal.show('modalAufgabeEdit');
    },
    onRowSelected() {
      this.allSelectedRows = this.getAllSelectedRows();
      console.log('this.allSelectedRow: ', this.allSelectedRow);
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setMarkedCount(this.allSelectedRows.length);
    },
    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi.forEachNode((node, index) => {
        if (node.selected) {
          rowsSelected.push(node);
        }
      });
      return rowsSelected;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

:deep(.ag-theme-alpine) {
  font-family: 'Poppins';
}

:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 500;
  font-size: 14px;
}

:deep(.ag-cell),
:deep(.ag-header-cell) {
  padding: 0px 0.5rem;
}

:deep(.ag-header-cell-text) {
  white-space: normal !important;
  line-height: 1.2;
}

:deep(.ag-icon-filter) {
  display: none !important;
}

:deep(.ag-header-cell-menu-button) {
  display: none !important;
}
</style>
